import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/utils/Functions";
import { PATH_ROOT } from "@/routes/paths";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import {
  PreviewCard,
  PreviewCardTrigger,
  PreviewCardPopup,
} from "@/components/ui/preview-card";
import { useCalendarData } from "@/hooks/calendar/useCalendarData";
import {
  getWeekDays,
  formatDateString,
} from "@/views/calendar";
import { formatTime } from "@/views/calendar/calendar-utils";
import type { CalendarEvent } from "@/types/calendar.types";

import { Widget } from "./Widget";
import { IconCalendarFilled } from "@/components/icons";
import type { DragHandleProps } from "../../constants";

const views = ["Day", "Week", "Month"] as const;
type View = (typeof views)[number];

const eventTypeDotColor: Record<CalendarEvent["type"], string> = {
  contract: "bg-violet-500",
  meeting: "bg-cyan-500",
  deadline: "bg-red-500",
  review: "bg-amber-500",
};

const slideVariants = {
  enter: (d: number) => ({ x: `${d * 100}%`, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d: number) => ({ x: `${d * -100}%`, opacity: 0 }),
};

const DayView = ({
  navigate,
  isDemo,
}: {
  navigate: (path: string) => void;
  isDemo: boolean;
}) => {
  const { events: calendarEvents, tasks: calendarTasks } = useCalendarData();
  const todayStr = formatDateString(new Date());
  const events = calendarEvents;
  const tasks = calendarTasks;

  const todayEvents = useMemo(
    () =>
      events
        .filter((e) => e.date === todayStr)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [todayStr, events],
  );

  const todayTasks = useMemo(
    () => tasks.filter((t) => t.dueDate === todayStr && !t.completed),
    [todayStr, tasks],
  );

  const dateLabel = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const allItems = [...todayEvents, ...todayTasks];
  const maxVisible = 4;
  const visibleEvents = todayEvents.slice(0, maxVisible);
  const remaining = allItems.length - visibleEvents.length;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-muted-foreground">{dateLabel}</p>

      {allItems.length === 0 && (
        <p className="text-xs text-muted-foreground py-3 text-center">No events today</p>
      )}

      {visibleEvents.map((event) => {
        return (
          <button
            key={event.id}
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.calendar : PATH_ROOT.main.calendar)}
            className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/50 transition-colors text-left cursor-pointer w-full"
          >
            <IconCalendarFilled className="size-4 text-muted-foreground/50" aria-hidden="true" />
            <span className="text-xs font-medium text-foreground truncate flex-1">{event.title}</span>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {formatTime(event.startTime)}
            </span>
          </button>
        );
      })}

      {remaining > 0 && (
        <button
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.calendar : PATH_ROOT.main.calendar)}
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors text-left px-2 py-1 cursor-pointer"
        >
          +{remaining} more
        </button>
      )}
    </div>
  );
};

const WeekView = ({
  navigate,
  isDemo,
}: {
  navigate: (path: string) => void;
  isDemo: boolean;
}) => {
  const { events: calendarEvents } = useCalendarData();
  const today = new Date();
  const todayStr = formatDateString(today);
  const weekDays = useMemo(() => getWeekDays(today), []);
  const events = calendarEvents;

  const eventsByDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const day of weekDays) {
      const key = formatDateString(day);
      map[key] = events
        .filter((e) => e.date === key)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    return map;
  }, [weekDays, events]);

  const weekLabel = useMemo(() => {
    const start = weekDays[0];
    const end = weekDays[6];
    if (!start || !end) return "";
    const startMonth = start.toLocaleDateString("en-US", { month: "short" });
    const endMonth = end.toLocaleDateString("en-US", { month: "short" });
    if (startMonth === endMonth) {
      return `${startMonth} ${start.getDate()} - ${end.getDate()}`;
    }
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
  }, [weekDays]);

  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs font-medium text-muted-foreground mb-1">{weekLabel}</p>

      {weekDays.map((day) => {
        const dayStr = formatDateString(day);
        const isToday = dayStr === todayStr;
        const events = eventsByDay[dayStr] || [];
        const dayLabel = day.toLocaleDateString("en-US", { weekday: "short" });
        const dateNum = day.getDate();
        const topEvent = events[0];

        return (
          <button
            key={dayStr}
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.calendar : PATH_ROOT.main.calendar)}
            className={cn(
              "flex items-center gap-2 py-1.5 px-2 rounded-lg transition-colors text-left cursor-pointer w-full",
              isToday ? "bg-primary/10 dark:bg-primary/15" : "hover:bg-muted/50",
            )}
          >
            <span
              className={cn(
                "text-[10px] uppercase font-medium w-7 shrink-0",
                isToday ? "text-primary" : "text-muted-foreground",
              )}
            >
              {dayLabel}
            </span>
            <span
              className={cn(
                "text-xs font-semibold w-5 shrink-0",
                isToday ? "text-primary" : "text-foreground",
              )}
            >
              {dateNum}
            </span>

            {events.length === 0 ? (
              <span className="text-[10px] text-muted-foreground/40 flex-1">-</span>
            ) : (
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <span className={cn("size-1.5 rounded-full shrink-0", eventTypeDotColor[topEvent.type])} />
                <span className="text-xs text-foreground truncate">{topEvent.title}</span>
                {events.length > 1 && (
                  <span className="text-[10px] text-muted-foreground shrink-0">+{events.length - 1}</span>
                )}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

const MonthView = ({
  navigate,
  isDemo,
}: {
  navigate: (path: string) => void;
  isDemo: boolean;
}) => {
  const { events: calendarEvents } = useCalendarData();
  const today = new Date();
  const todayStr = formatDateString(today);
  const events = calendarEvents;

  const monthEvents = useMemo(() => {
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const prefix = `${year}-${month}`;
    return events.filter((e) => e.date.startsWith(prefix));
  }, [today, events]);

  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const event of monthEvents) {
      if (!map[event.date]) map[event.date] = [];
      map[event.date].push(event);
    }
    return map;
  }, [monthEvents]);

  const { weeks, monthLabel } = useMemo(() => {
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();

    const label = today.toLocaleDateString("en-US", { month: "long", year: "numeric" });

    const cells: (number | null)[] = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const w: (number | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      w.push(cells.slice(i, i + 7));
    }

    return { weeks: w, monthLabel: label };
  }, [today]);

  const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">{monthLabel}</p>

      <div className="grid grid-cols-7 gap-px">
        {dayNames.map((d, i) => (
          <div key={i} className="text-center text-[10px] text-muted-foreground/60 font-medium pb-1">
            {d}
          </div>
        ))}
        {weeks.flat().map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;

          const dayKey = formatDateString(new Date(today.getFullYear(), today.getMonth(), day));
          const events = eventsByDate[dayKey] || [];
          const isToday = dayKey === todayStr;

          const dayButton = (
            <button
              onClick={() => navigate(isDemo ? PATH_ROOT.demo.calendar : PATH_ROOT.main.calendar)}
              className={cn(
                "flex flex-col items-center py-1 rounded-md text-xs transition-colors cursor-pointer w-full",
                isToday
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "text-muted-foreground/60",
              )}
            >
              <span>{day}</span>
              {events.length > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {events.slice(0, 3).map((e) => (
                    <span
                      key={e.id}
                      className={cn(
                        "size-1 rounded-full",
                        isToday ? "bg-primary-foreground/70" : eventTypeDotColor[e.type],
                      )}
                    />
                  ))}
                </div>
              )}
            </button>
          );

          if (events.length === 0) {
            return <div key={dayKey}>{dayButton}</div>;
          }

          return (
            <PreviewCard key={dayKey}>
              <PreviewCardTrigger className="w-full" render={<div />}>
                {dayButton}
              </PreviewCardTrigger>
              <PreviewCardPopup sideOffset={8} className="w-fit p-0">
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-xs font-semibold text-foreground">
                    {new Date(today.getFullYear(), today.getMonth(), day).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <div className="flex flex-col py-1">
                  {events.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => navigate(isDemo ? PATH_ROOT.demo.calendar : PATH_ROOT.main.calendar)}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 transition-colors text-left"
                    >
                      <span className={cn("size-1.5 rounded-full shrink-0", eventTypeDotColor[event.type])} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{event.title}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatTime(event.startTime)} - {formatTime(event.endTime)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </PreviewCardPopup>
            </PreviewCard>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t border-border/40">
        <span>{monthEvents.length} events this month</span>
        <span>{Object.keys(eventsByDate).length} days with events</span>
      </div>
    </div>
  );
};

export const CalendarWidget = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => {
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const [viewIndex, setViewIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const currentView = views[viewIndex] as View;

  const next = useCallback(() => {
    setDirection(1);
    setViewIndex((i) => (i + 1) % views.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setViewIndex((i) => (i - 1 + views.length) % views.length);
  }, []);

  const goTo = useCallback(
    (idx: number) => {
      setDirection(idx > viewIndex ? 1 : -1);
      setViewIndex(idx);
    },
    [viewIndex],
  );

  return (
    <Widget
      title="Calendar"
      icon={<CalendarIcon className="size-5" aria-hidden="true" />}
      linkTo={isDemo ? PATH_ROOT.demo.calendar : PATH_ROOT.main.calendar}
      dragHandleProps={dragHandleProps}
    >
      <div className="flex flex-col flex-1 min-h-0 gap-3">
        <div className="flex items-center gap-1 bg-neutral-200 dark:bg-neutral-800 rounded-lg p-0.5">
          {views.map((view, i) => (
            <button
              key={view}
              onClick={() => goTo(i)}
              className={cn(
                "flex-1 text-xs font-medium py-1.5 rounded-md transition-colors",
                i === viewIndex
                  ? "bg-white dark:bg-neutral-700 text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {view}
            </button>
          ))}
        </div>

        <div className="relative flex-1 overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.div
              key={currentView}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              {currentView === "Day" && <DayView navigate={navigate} isDemo={isDemo} />}
              {currentView === "Week" && <WeekView navigate={navigate} isDemo={isDemo} />}
              {currentView === "Month" && <MonthView navigate={navigate} isDemo={isDemo} />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {views.map((_, i) => (
              <motion.div
                key={i}
                className="h-1.5 rounded-full"
                animate={{
                  width: i === viewIndex ? 20 : 6,
                  backgroundColor:
                    i === viewIndex
                      ? "var(--color-neutral-800)"
                      : "var(--color-neutral-300)",
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              />
            ))}
          </div>
          <div className="flex gap-1">
            <button
              onClick={prev}
              className="grid size-7 place-items-center rounded-lg border border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-white"
            >
              <ChevronLeft className="size-3.5" />
            </button>
            <button
              onClick={next}
              className="grid size-7 place-items-center rounded-lg border border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-white"
            >
              <ChevronRight className="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </Widget>
  );
};
